import { morph } from '@theme/morph';

/**
 * <pack-switcher>
 *
 * Consolidates the 1 / 2 / 3 pack products onto a single product page. Each
 * pack tile links to a real product URL (SEO + no-JS fallback). When clicked,
 * we fetch that product page and morph the current <main> into it — no full
 * reload — reusing the theme's own morph engine, the same mechanism the variant
 * picker uses for combined listings. Because morph re-runs each component's
 * updatedCallback, the variant picker, media gallery and buy buttons all
 * re-initialise for the newly selected pack automatically.
 */
class PackSwitcher extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);

    if (!PackSwitcher.#popStateBound) {
      PackSwitcher.#popStateBound = true;
      window.addEventListener('popstate', PackSwitcher.#handlePopState);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    const tile = event.target.closest('[data-pack-option]');
    if (!tile || !this.contains(tile)) return;

    // Preserve native behaviour for open-in-new-tab / modified clicks.
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();

    // Already on this pack — nothing to do.
    if (tile.getAttribute('aria-current') === 'true') return;

    const url = tile.getAttribute('href');
    if (url) PackSwitcher.load(url, true);
  };

  static #popStateBound = false;

  static #handlePopState = () => {
    if (document.querySelector('pack-switcher')) {
      PackSwitcher.load(window.location.href, false);
    }
  };

  /** @type {AbortController | undefined} */
  static #abortController;

  /**
   * Fetch a pack product page and morph the product section into place.
   *
   * We deliberately morph only the product-information section rather than the
   * whole <main>, so third-party app DOM injected elsewhere on the page is left
   * untouched and the swap stays fast and focused on the buy box + gallery.
   *
   * @param {string} url - The target product URL.
   * @param {boolean} push - Whether to push a new history entry.
   */
  static async load(url, push) {
    const switcher = document.querySelector('pack-switcher');
    const section = switcher?.closest('.shopify-section') || document.querySelector('main');
    if (!section) {
      window.location.assign(url);
      return;
    }

    PackSwitcher.#abortController?.abort();
    PackSwitcher.#abortController = new AbortController();

    const main = document.querySelector('main');
    main?.setAttribute('data-pack-loading', 'true');

    try {
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'PackSwitcher' },
        signal: PackSwitcher.#abortController.signal,
      });
      if (!response.ok) throw new Error(`Pack request failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      // The section id is template-specific (e.g. ...template--123__product-information),
      // so match by the section that contains the pack switcher rather than by id.
      const newSection = doc.querySelector('pack-switcher')?.closest('.shopify-section');
      if (!newSection) throw new Error('No matching product section in pack response');

      morph(section, newSection);

      const newTitle = doc.querySelector('title')?.textContent;
      if (newTitle) document.title = newTitle;

      if (push) {
        history.pushState({ packSwitcher: true }, '', url.split('?')[0]);
      }

      // Bring the buy box back into view on mobile, where the switch happens
      // below the fold after scrolling.
      if (window.matchMedia('(max-width: 749px)').matches) {
        document.querySelector('pack-switcher')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[pack-switcher]', error);
      window.location.assign(url);
    } finally {
      document.querySelector('main')?.removeAttribute('data-pack-loading');
    }
  }
}

if (!customElements.get('pack-switcher')) {
  customElements.define('pack-switcher', PackSwitcher);
}
